import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  MapPin,
  Clock,
  ArrowRight,
  Briefcase,
  Users,
  TrendingUp,
  Award,
  HeartHandshake,
  X,
  Loader2,
  Upload,
  CheckCircle2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_ORANGE = '#FF6600';
const BRAND_ORANGE_DARK = '#E65C00';
const CHARCOAL = '#222222';
const BORDER = '#E5E7EB';
const SURFACE = '#F5F5F5';

/* ============================================================
   Helper components
   ============================================================ */

function BulletList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="space-y-2">
      {items.map((r, i) => (
        <li key={i} className="flex gap-2 text-sm sm:text-base text-[#222222]/80 leading-relaxed">
          <span
            className="flex-shrink-0 mt-1.5 h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: BRAND_ORANGE }}
          />
          <span>{r}</span>
        </li>
      ))}
    </ul>
  );
}


/* ============================================================
   Application Form (used inside JobModal)
   ============================================================ */

function ApplicationForm({ job, onSuccess, onCancel }) {
  const [values, setValues] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    location: '',
    right_to_work: '',
    cover_letter: '',
    linkedin: '',
    honeypot: '',
  });
  const [consent, setConsent] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const fileInputRef = useRef(null);

  function updateField(name, val) {
    setValues((v) => ({ ...v, [name]: val }));
    if (fieldError[name]) setFieldError((e) => ({ ...e, [name]: null }));
  }

  function validate() {
    const errs = {};
    if (!values.first_name.trim()) errs.first_name = 'Required.';
    if (!values.last_name.trim()) errs.last_name = 'Required.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email.trim()))
      errs.email = 'Please enter a valid email.';
    if (values.phone.replace(/\D/g, '').length < 6) errs.phone = 'Please enter a valid phone number.';
    if (!cvFile) errs.cv = 'CV upload is required (PDF, DOC or DOCX, max 5MB).';
    else {
      const ext = cvFile.name.split('.').pop()?.toLowerCase();
      if (!['pdf', 'doc', 'docx'].includes(ext)) errs.cv = 'CV must be PDF, DOC or DOCX.';
      else if (cvFile.size > 5 * 1024 * 1024) errs.cv = 'CV file must be under 5 MB.';
    }
    if (!consent) errs.consent = 'Please confirm the recruitment privacy statement.';
    setFieldError(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setSubmitting(true);
    const fd = new FormData();
    fd.append('job_id', job.id);
    Object.entries(values).forEach(([k, v]) => fd.append(k, v || ''));
    fd.append('consent', consent ? 'true' : 'false');
    fd.append('cv', cvFile, cvFile.name);
    try {
      const res = await axios.post(`${API}/careers/applications`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted({ id: res.data.application_id, message: res.data.message });
      onSuccess && onSuccess(res.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          'We could not process your application. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div
        data-testid="careers-application-success"
        className="text-center py-8 sm:py-12 px-4"
      >
        <div className="mx-auto mb-4 h-14 w-14 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND_ORANGE }}>
          <CheckCircle2 className="h-8 w-8 text-white" />
        </div>
        <h4 className="text-xl sm:text-2xl font-bold mb-3" style={{ color: CHARCOAL }}>
          Application received
        </h4>
        <p className="max-w-lg mx-auto text-sm sm:text-base text-[#222222]/80 leading-relaxed mb-6">
          {submitted.message}
        </p>
        <p className="text-xs text-[#222222]/50">Reference: {submitted.id}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="careers-application-form" className="space-y-5" noValidate>
      {error && (
        <div role="alert" className="text-sm p-3 rounded-lg border" style={{ borderColor: '#FFC28A', backgroundColor: '#FFF3E8', color: CHARCOAL }}>
          {error}
        </div>
      )}

      {/* Honeypot — hidden from humans */}
      <input
        type="text"
        name="honeypot"
        value={values.honeypot}
        onChange={(e) => updateField('honeypot', e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="First name *" error={fieldError.first_name}>
          <input
            data-testid="careers-first-name"
            type="text" required
            value={values.first_name}
            onChange={(e) => updateField('first_name', e.target.value)}
            className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600]/40 focus:border-[#FF6600]"
            style={{ borderColor: fieldError.first_name ? BRAND_ORANGE : BORDER }}
          />
        </Field>
        <Field label="Last name *" error={fieldError.last_name}>
          <input
            data-testid="careers-last-name"
            type="text" required
            value={values.last_name}
            onChange={(e) => updateField('last_name', e.target.value)}
            className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600]/40 focus:border-[#FF6600]"
            style={{ borderColor: fieldError.last_name ? BRAND_ORANGE : BORDER }}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Email address *" error={fieldError.email}>
          <input
            data-testid="careers-email"
            type="email" required
            value={values.email}
            onChange={(e) => updateField('email', e.target.value)}
            className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600]/40 focus:border-[#FF6600]"
            style={{ borderColor: fieldError.email ? BRAND_ORANGE : BORDER }}
          />
        </Field>
        <Field label="Phone number *" error={fieldError.phone}>
          <input
            data-testid="careers-phone"
            type="tel" required
            value={values.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600]/40 focus:border-[#FF6600]"
            style={{ borderColor: fieldError.phone ? BRAND_ORANGE : BORDER }}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Current location">
          <input
            data-testid="careers-location"
            type="text"
            value={values.location}
            onChange={(e) => updateField('location', e.target.value)}
            placeholder="e.g. London, UK"
            className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600]/40 focus:border-[#FF6600]"
            style={{ borderColor: BORDER }}
          />
        </Field>
        <Field label="Right to work in the UK">
          <select
            data-testid="careers-rtw"
            value={values.right_to_work}
            onChange={(e) => updateField('right_to_work', e.target.value)}
            className="w-full px-3 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]/40 focus:border-[#FF6600]"
            style={{ borderColor: BORDER }}
          >
            <option value="">Please choose…</option>
            <option value="yes">Yes, unrestricted</option>
            <option value="visa">Yes, with a valid visa</option>
            <option value="sponsorship">Sponsorship required</option>
            <option value="no">No, sponsorship required</option>
          </select>
        </Field>
      </div>

      <Field label="Job applied for">
        <input
          type="text"
          readOnly
          value={job.title}
          className="w-full px-3 py-2.5 border rounded-lg bg-[#F5F5F5]"
          style={{ borderColor: BORDER, color: CHARCOAL }}
        />
      </Field>

      <Field label="CV upload (PDF, DOC, DOCX — max 5MB) *" error={fieldError.cv}>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="careers-cv-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 border rounded-lg font-semibold text-sm bg-white hover:bg-[#F5F5F5]"
            style={{ borderColor: BORDER, color: CHARCOAL }}
          >
            <Upload className="h-4 w-4" style={{ color: BRAND_ORANGE }} />
            {cvFile ? 'Change file' : 'Choose file'}
          </button>
          <input
            ref={fileInputRef}
            data-testid="careers-cv-input"
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="sr-only"
            onChange={(e) => setCvFile(e.target.files?.[0] || null)}
          />
          {cvFile && (
            <span className="text-sm text-[#222222]/80 truncate max-w-full sm:max-w-[300px]">
              {cvFile.name} · {(cvFile.size / 1024 / 1024).toFixed(2)} MB
            </span>
          )}
        </div>
      </Field>

      <Field label="Cover letter / message">
        <textarea
          data-testid="careers-cover-letter"
          rows={4}
          value={values.cover_letter}
          onChange={(e) => updateField('cover_letter', e.target.value)}
          placeholder="Tell us why you'd be a great fit."
          className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600]/40 focus:border-[#FF6600]"
          style={{ borderColor: BORDER }}
        />
      </Field>

      <Field label="LinkedIn profile (optional)">
        <input
          data-testid="careers-linkedin"
          type="url"
          value={values.linkedin}
          onChange={(e) => updateField('linkedin', e.target.value)}
          placeholder="https://linkedin.com/in/…"
          className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600]/40 focus:border-[#FF6600]"
          style={{ borderColor: BORDER }}
        />
      </Field>

      <label
        className="flex items-start gap-3 text-sm text-[#222222]/80 leading-relaxed cursor-pointer select-none"
        data-testid="careers-consent-label"
      >
        <input
          data-testid="careers-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 border rounded"
          style={{ borderColor: BORDER, accentColor: BRAND_ORANGE }}
        />
        <span>
          I confirm that the information I have provided is accurate and I
          consent to Flight380 Ltd processing my personal information for
          recruitment purposes. See our{' '}
          <a href="/privacy" target="_blank" rel="noopener" className="underline" style={{ color: BRAND_ORANGE }}>
            Privacy Policy
          </a>.
        </span>
      </label>
      {fieldError.consent && (
        <p className="text-xs -mt-2" style={{ color: BRAND_ORANGE }}>{fieldError.consent}</p>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid="careers-submit"
          className="inline-flex items-center justify-center gap-2 font-bold px-6 py-3 rounded-lg text-white transition-colors disabled:opacity-60"
          style={{ backgroundColor: BRAND_ORANGE }}
          onMouseEnter={(e) => !submitting && (e.currentTarget.style.backgroundColor = BRAND_ORANGE_DARK)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND_ORANGE)}
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {submitting ? 'Submitting…' : 'Submit Application'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center justify-center gap-2 font-semibold px-6 py-3 rounded-lg border bg-white"
          style={{ borderColor: BORDER, color: CHARCOAL }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold mb-1.5" style={{ color: CHARCOAL }}>
        {label}
      </span>
      {children}
      {error && (
        <span className="block mt-1 text-xs" style={{ color: BRAND_ORANGE }}>
          {error}
        </span>
      )}
    </label>
  );
}


/* ============================================================
   Job Detail Modal
   ============================================================ */

function JobModal({ job, initialTab = 'details', onClose }) {
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  if (!job) return null;
  return (
    <div
      className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center bg-black/60 p-0 sm:p-4"
      data-testid="job-detail-modal"
      onClick={onClose}
    >
      <div
        role="dialog" aria-modal="true"
        aria-labelledby="job-modal-title"
        className="bg-white w-full sm:max-w-3xl max-h-[100vh] sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-2xl shadow-xl border relative"
        style={{ borderColor: BORDER }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close job details"
          data-testid="job-modal-close"
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-[#F5F5F5]"
        >
          <X className="h-5 w-5" style={{ color: CHARCOAL }} />
        </button>
        <div className="p-6 sm:p-8 border-b" style={{ borderColor: BORDER }}>
          <span className="inline-block text-xs font-bold uppercase tracking-widest mb-2" style={{ color: BRAND_ORANGE }}>
            {job.department || 'Vacancy'}
          </span>
          <h2 id="job-modal-title" className="text-2xl sm:text-3xl font-bold leading-tight mb-3" style={{ color: CHARCOAL }}>
            {job.title}
          </h2>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#222222]/70">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4" style={{ color: BRAND_ORANGE }} />
              {job.location}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" style={{ color: BRAND_ORANGE }} />
              {job.employment_type}
            </span>
            {job.salary && <span className="font-medium" style={{ color: CHARCOAL }}>{job.salary}</span>}
          </div>
        </div>

        <div className="border-b sticky top-0 bg-white z-10" style={{ borderColor: BORDER }}>
          <div className="flex gap-2 px-4 sm:px-8">
            {[
              { k: 'details', label: 'Job Details' },
              { k: 'apply', label: 'Apply Now' },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`job-modal-tab-${t.k}`}
                className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  tab === t.k ? 'border-[#FF6600] text-[#FF6600]' : 'border-transparent text-[#222222]/70 hover:text-[#222222]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 sm:p-8">
          {tab === 'details' && (
            <div className="space-y-6">
              <Section title="About the role">
                <p className="text-sm sm:text-base text-[#222222]/80 leading-relaxed whitespace-pre-line">
                  {job.description || job.short_description}
                </p>
              </Section>
              <Section title="Key responsibilities">
                <BulletList items={job.responsibilities} />
              </Section>
              <Section title="Skills & experience">
                <BulletList items={job.skills} />
              </Section>
              {(job.benefits && job.benefits.length > 0) && (
                <Section title="Salary & benefits">
                  <BulletList items={job.benefits} />
                </Section>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <Fact label="Working hours" value={job.working_hours || '—'} />
                <Fact label="Location" value={job.location || '—'} />
                <Fact label="Reference" value={job.reference || '—'} />
                <Fact label="Closing date" value={job.closing_date || 'Open until filled'} />
              </div>
              <div className="border-t pt-6" style={{ borderColor: BORDER }}>
                <h4 className="font-bold mb-2" style={{ color: CHARCOAL }}>How to apply</h4>
                <p className="text-sm text-[#222222]/80 leading-relaxed mb-4">
                  Click <strong>Apply Now</strong> below to submit your application and upload your CV.
                </p>
                <button
                  onClick={() => setTab('apply')}
                  data-testid={`job-modal-apply-cta-${job.slug || job.id}`}
                  className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-lg text-white"
                  style={{ backgroundColor: BRAND_ORANGE }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND_ORANGE_DARK)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND_ORANGE)}
                >
                  Apply Now <ArrowRight className="h-4 w-4" />
                </button>
                <p className="text-xs text-[#222222]/50 mt-4">
                  Flight380 Ltd is an equal opportunities employer. We welcome applications
                  from all backgrounds and evaluate every candidate on merit.
                </p>
              </div>
            </div>
          )}
          {tab === 'apply' && (
            <ApplicationForm
              job={job}
              onSuccess={() => {}}
              onCancel={() => setTab('details')}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: CHARCOAL }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="border rounded-lg p-3" style={{ borderColor: BORDER, backgroundColor: SURFACE }}>
      <div className="text-xs uppercase font-semibold text-[#222222]/60 mb-1">{label}</div>
      <div className="text-sm font-medium" style={{ color: CHARCOAL }}>{value}</div>
    </div>
  );
}


/* ============================================================
   Job Card
   ============================================================ */

function JobCard({ job, onView }) {
  return (
    <article
      data-testid={`job-card-${job.slug || job.id}`}
      className="bg-white border rounded-2xl p-6 sm:p-7 flex flex-col"
      style={{ borderColor: BORDER }}
    >
      <div className="flex-1">
        <span className="inline-block text-xs font-bold uppercase tracking-widest mb-2" style={{ color: BRAND_ORANGE }}>
          {job.department || 'Vacancy'}
        </span>
        <h3 className="text-lg sm:text-xl font-bold leading-tight mb-3" style={{ color: CHARCOAL }}>
          {job.title}
        </h3>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#222222]/70 mb-4">
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-4 w-4" style={{ color: BRAND_ORANGE }} />
            {job.location}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-4 w-4" style={{ color: BRAND_ORANGE }} />
            {job.employment_type}
          </span>
        </div>
        {job.salary && (
          <div className="text-sm font-medium mb-3" style={{ color: CHARCOAL }}>
            {job.salary}
          </div>
        )}
        <p className="text-sm text-[#222222]/80 leading-relaxed">
          {job.short_description || job.description}
        </p>
      </div>
      <div className="mt-6 flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => onView(job, 'details')}
          data-testid={`view-job-${job.slug || job.id}`}
          className="flex-1 inline-flex items-center justify-center gap-2 font-semibold px-5 py-2.5 rounded-lg border bg-white hover:bg-[#F5F5F5]"
          style={{ borderColor: BORDER, color: CHARCOAL }}
        >
          View Job
        </button>
        <button
          onClick={() => onView(job, 'apply')}
          data-testid={`apply-job-${job.slug || job.id}`}
          className="flex-1 inline-flex items-center justify-center gap-2 font-bold px-5 py-2.5 rounded-lg text-white transition-colors"
          style={{ backgroundColor: BRAND_ORANGE }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND_ORANGE_DARK)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND_ORANGE)}
        >
          Apply Now <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}


/* ============================================================
   Main Page
   ============================================================ */

const WHY_JOIN = [
  { icon: TrendingUp, title: 'Career Development', body: 'Grow your skills and develop your career within a growing travel business.' },
  { icon: Briefcase, title: 'Travel Industry Experience', body: 'Gain valuable experience working within the UK and international travel sector.' },
  { icon: Users, title: 'Team Environment', body: 'Work with an ambitious and customer-focused team.' },
  { icon: Award, title: 'Growing Business', body: 'Become part of a company developing its B2C and B2B travel operations.' },
  { icon: HeartHandshake, title: 'Customer Focus', body: 'Help provide professional travel services to customers and travel partners.' },
];

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const applyRef = useRef(null);
  const vacanciesRef = useRef(null);

  // SEO
  useEffect(() => {
    document.title = 'Careers at Flight380 | Travel Jobs in the UK';
    const md = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'description');
      document.head.appendChild(m);
      return m;
    })();
    md.setAttribute('content', 'Explore career opportunities with Flight380 Ltd. Discover current vacancies and join our growing UK travel business.');
  }, []);

  // Fetch jobs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/careers/jobs`);
        if (!cancelled) setJobs(res.data?.jobs || []);
      } catch (e) {
        if (!cancelled) setLoadError('Could not load current vacancies. Please try again shortly.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // JobPosting JSON-LD
  const jsonLd = useMemo(() => {
    if (!jobs.length) return null;
    return jobs.map((j) => ({
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: j.title,
      description: j.description || j.short_description,
      datePosted: j.created_at,
      validThrough: j.closing_date || undefined,
      employmentType: (j.employment_type || 'FULL_TIME').toUpperCase().replace(/[- ]/g, '_'),
      hiringOrganization: {
        '@type': 'Organization',
        name: 'Flight380 Ltd',
        sameAs: 'https://www.flight380.co.uk',
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: j.location || 'Luton',
          addressCountry: 'GB',
        },
      },
    }));
  }, [jobs]);

  function openJob(job, initialTab = 'details') {
    setSelectedJob({ job, initialTab });
  }

  function scrollToVacancies() {
    vacanciesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function scrollToApply() {
    if (jobs[0]) openJob(jobs[0], 'apply');
    else vacanciesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <main
      data-testid="careers-page"
      className="min-h-screen bg-white"
      style={{ paddingTop: '160px' }}
    >
      {jsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}

      {/* HERO */}
      <section className="bg-white border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-bold uppercase tracking-widest mb-4" style={{ color: BRAND_ORANGE }}>
              Careers at Flight380
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4" style={{ color: CHARCOAL }}>
              Build Your Career with Flight380
            </h1>
            <p className="text-base sm:text-lg text-[#222222]/80 leading-relaxed">
              Join a growing UK travel company and help us make travel simpler,
              smarter and more accessible for customers and travel partners.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={scrollToVacancies}
                data-testid="careers-hero-view-vacancies"
                className="inline-flex items-center justify-center gap-2 font-bold px-6 py-3 rounded-lg text-white transition-colors"
                style={{ backgroundColor: BRAND_ORANGE }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND_ORANGE_DARK)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND_ORANGE)}
              >
                View Current Vacancies <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={scrollToApply}
                data-testid="careers-hero-send-cv"
                className="inline-flex items-center justify-center gap-2 font-semibold px-6 py-3 rounded-lg border bg-white hover:bg-[#F5F5F5]"
                style={{ borderColor: BORDER, color: CHARCOAL }}
              >
                Send Your CV
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* WHY JOIN */}
      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8" style={{ color: CHARCOAL }}>
            Why Join Flight380?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {WHY_JOIN.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="border rounded-xl p-6 bg-white"
                  style={{ borderColor: BORDER }}
                  data-testid={`why-card-${v.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="h-10 w-10 rounded-lg mb-4 flex items-center justify-center" style={{ backgroundColor: '#FFF3E8' }}>
                    <Icon className="h-5 w-5" style={{ color: BRAND_ORANGE }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: CHARCOAL }}>{v.title}</h3>
                  <p className="text-sm text-[#222222]/80 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VACANCIES */}
      <section
        id="open-roles"
        ref={vacanciesRef}
        className="bg-[#F5F5F5] border-t"
        style={{ borderColor: BORDER }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: CHARCOAL }}>
                Current Vacancies
              </h2>
              <p className="text-sm text-[#222222]/70 mt-1" data-testid="careers-vacancy-count">
                {loading ? 'Loading…' : `${jobs.length} position${jobs.length === 1 ? '' : 's'} currently open`}
              </p>
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-[#222222]/70">
              <Loader2 className="h-4 w-4 animate-spin" style={{ color: BRAND_ORANGE }} />
              Loading vacancies…
            </div>
          )}
          {loadError && !loading && (
            <div className="border rounded-lg p-4 bg-white" style={{ borderColor: BORDER, color: CHARCOAL }} role="alert">
              {loadError}
            </div>
          )}
          {!loading && !loadError && jobs.length === 0 && (
            <div className="border rounded-lg p-6 bg-white text-center" style={{ borderColor: BORDER }}>
              <p className="text-[#222222]/80">
                We don't have any open roles right now — but we're always keen to hear from great candidates.
                Use <strong>Send Your CV</strong> above to send us a speculative application.
              </p>
            </div>
          )}
          {!loading && jobs.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6" data-testid="careers-jobs-list">
              {jobs.map((job) => (
                <JobCard key={job.id || job.slug} job={job} onView={openJob} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Speculative CTA */}
      <section
        ref={applyRef}
        id="apply"
        className="border-t"
        style={{ borderColor: BORDER, backgroundColor: CHARCOAL }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Don't see the right role?
          </h2>
          <p className="text-sm sm:text-base text-white/80 mb-6 max-w-2xl mx-auto">
            We're growing. If you think you could help us serve our customers
            better — pick the closest open role above and select <strong>Apply Now</strong>,
            or email us at{' '}
            <a href="mailto:careers@flight380.co.uk" className="underline" style={{ color: BRAND_ORANGE }}>
              careers@flight380.co.uk
            </a>.
          </p>
          <button
            onClick={scrollToVacancies}
            className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-lg text-white transition-colors"
            style={{ backgroundColor: BRAND_ORANGE }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND_ORANGE_DARK)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND_ORANGE)}
          >
            See Open Roles <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      {selectedJob && (
        <JobModalWithInitialTab
          job={selectedJob.job}
          initialTab={selectedJob.initialTab}
          onClose={() => setSelectedJob(null)}
        />
      )}
    </main>
  );
}


/* Wrapper so the modal opens on the correct initial tab. */
function JobModalWithInitialTab({ job, initialTab, onClose }) {
  const [key, setKey] = useState(0);
  useEffect(() => {
    setKey((k) => k + 1);
  }, [job?.id, initialTab]);
  return (
    <JobModalStateful key={key} job={job} initialTab={initialTab} onClose={onClose} />
  );
}

function JobModalStateful({ job, initialTab, onClose }) {
  // Simple pass-through with initial-tab wired in.
  return (
    <JobModal
      job={job}
      initialTab={initialTab}
      onClose={onClose}
    />
  );
}
